import React, { useState } from "react";
import LegalHeader from "../components/LegalHeader";
import Footer from "../components/Footer";

export default function TermsAndConditions() {
  const [activeSection, setActiveSection] = useState<string>("intro");

  const scrollTo = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -90;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const sections = [
    { id: "intro", title: "1. Acceptance of Terms & Eligibility" },
    { id: "marketplace", title: "2. The Fixezy Marketplace Model" },
    { id: "account", title: "3. Account Registration & Verification" },
    { id: "booking", title: "4. Bookings & Diagnostic Inspections" },
    { id: "pricing", title: "5. Pricing, Rate Cards & Payments" },
    { id: "cancellation", title: "6. Cancellation, Rescheduling & Refunds" },
    { id: "warranty", title: "7. 30-Day Service Warranty & Revisit" },
    { id: "conduct", title: "8. User Conduct & Workplace Safety" },
    { id: "partner", title: "9. Service Partner Independence" },
    { id: "liability", title: "10. Limitation of Liability" },
    { id: "ip", title: "11. Intellectual Property" },
    { id: "governing-law", title: "12. Governing Law & Jurisdiction" },
    { id: "contact", title: "13. Contact Information" },
  ];

  return (
    <div className="w-full bg-white min-h-screen flex flex-col font-['Roboto',sans-serif]">
      <LegalHeader />

      {/* Header Title Section */}
      <section className="border-b border-gray-200 py-10 px-6">
        <div className="max-w-[1100px] mx-auto">
          <p className="text-xs text-gray-500 mb-2">Fixezy / Legal / Terms & Conditions</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            Terms & Conditions
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Last updated: October 2026 • Governing Fixezy customer apps and website
          </p>
        </div>
      </section>

      {/* Main Content Layout */}
      <div className="max-w-[1100px] mx-auto px-6 py-10 pb-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Table of Contents - Clean simple text links */}
          <aside className="hidden lg:block lg:col-span-4 sticky top-28 border border-gray-200 rounded-lg p-5 bg-white">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">
              Table of Contents
            </p>
            <nav className="flex flex-col space-y-2">
              {sections.map((section) => (
                <p
                  key={section.id}
                  onClick={() => scrollTo(section.id)}
                  className={`cursor-pointer text-xs transition-colors ${activeSection === section.id
                      ? "text-black font-semibold"
                      : "text-gray-600 hover:text-black"
                    }`}
                >
                  {section.title}
                </p>
              ))}
            </nav>
          </aside>

          {/* Legal Text Area */}
          <main className="lg:col-span-8 text-gray-700 space-y-10 leading-relaxed text-sm sm:text-base">
            {/* Section 1 */}
            <section id="intro" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                1. Acceptance of Terms & Eligibility
              </h2>
              <p>
                These Terms and Conditions ("Terms") govern your access to and use of the Fixezy website (fixezy.in), the Fixezy Customer mobile app on Android and iOS, and related booking services operated by Fixezy Private Limited ("Fixezy", "we", "us", or "our"), headquartered in Vijayanagar, Bengaluru, Karnataka, India.
              </p>
              <p className="mt-3">
                By creating an account, accessing our applications, or booking any service, you confirm that:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-gray-700">
                <li>You are at least 18 years old and capable of entering into legally binding agreements under Indian law.</li>
                <li>You are the authorized resident or owner of the premises where services are requested.</li>
                <li>You agree to comply fully with these Terms and our companion Privacy Policy.</li>
              </ul>
            </section>

            {/* Section 2 */}
            <section id="marketplace" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                2. The Fixezy Marketplace Model
              </h2>
              <p>
                Fixezy operates as a technology intermediary under Section 79 of the Information Technology Act, 2000. Fixezy connects customers with independent skilled professionals ("Service Partners" or "Technicians") who deliver on-demand home maintenance, appliance repair, plumbing, and cleaning services.
              </p>
              <p className="mt-3">
                Unless explicitly stated in writing, Fixezy does not directly employ the Service Partners. Service Partners operate as independent contractors. Fixezy provides platform scheduling, identity vetting, transparent payment processing, and our service quality guarantee.
              </p>
            </section>

            {/* Section 3 */}
            <section id="account" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                3. Account Registration & Verification
              </h2>
              <p>
                Accounts are registered using a valid 10-digit Indian mobile number verified via SMS One-Time Password (OTP). You agree to provide accurate, current, and complete personal and address details. You are responsible for maintaining the confidentiality of your account on your device.
              </p>
            </section>

            {/* Section 4 */}
            <section id="booking" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                4. Bookings & Diagnostic Inspections
              </h2>
              <p>
                When you schedule an appointment:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1.5 text-gray-700">
                <li>
                  <strong>Inspection & Diagnostic Visit:</strong> For repairs, a technician will visit your location to inspect the appliance or fixture. A standard visiting/inspection charge applies, which is deducted from your final bill if you proceed with the repair.
                </li>
                <li>
                  <strong>Digital Job Sheet & Quotation Approval:</strong> Prior to replacing parts or commencing repairs, the technician presents a digital quotation. Work proceeds only after you review and approve the estimate.
                </li>
                <li>
                  <strong>Adult Presence:</strong> A responsible adult aged 18 or older must remain at the premises throughout the service visit.
                </li>
              </ul>
            </section>

            {/* Section 5 */}
            <section id="pricing" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                5. Pricing, Rate Cards & Payments
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Standard service charges are displayed in the app prior to booking. Extra spare parts or additional work are quoted transparently based on standard rate cards.</li>
                <li>All applicable Goods and Services Tax (GST) is itemized on the final tax invoice.</li>
                <li>Payment can be completed online via UPI, Credit/Debit card, Netbanking, or in cash upon job completion against a digital receipt generated in the app.</li>
                <li>All work must be recorded in the official app job sheet. Work arranged outside the app is not covered under Fixezy's warranty or support.</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section id="cancellation" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                6. Cancellation, Rescheduling & Refunds
              </h2>
              <p>
                We maintain simple and fair cancellation guidelines:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1.5 text-gray-700">
                <li>
                  <strong>Free Cancellation:</strong> You may cancel or reschedule free of charge up to 2 hours before the scheduled appointment window.
                </li>
                <li>
                  <strong>Late Cancellation Fee:</strong> If a cancellation is requested after a technician has already been dispatched or within 30 minutes of the slot, a nominal convenience fee (up to ₹99) may apply to compensate the partner's travel.
                </li>
                <li>
                  <strong>Refunds:</strong> For prepaid online bookings cancelled within the eligible window, refunds are credited back to your original payment method within 5 to 7 business days.
                </li>
              </ul>
            </section>

            {/* Section 7 */}
            <section id="warranty" className="scroll-mt-28 border-t border-gray-200 pt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                7. 30-Day Service Warranty & Revisit Guarantee
              </h2>
              <p>
                Fixezy provides a <strong>30-Day Revisit Guarantee</strong> on eligible repair jobs:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1.5 text-gray-700">
                <li>If the exact same issue reoccurs within 30 days of service completion, we dispatch a technician to inspect and rectify the issue without charging additional labor fees.</li>
                <li><strong>Exclusions:</strong> The warranty does not cover issues resulting from external power surges, physical mishandling, unauthorized third-party repairs after our visit, or normal wear and tear on aged appliances.</li>
                <li>Replacement spare parts carry the respective spare part manufacturer's warranty.</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section id="conduct" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                8. User Conduct & Workplace Safety
              </h2>
              <p>
                Fixezy maintains a zero-tolerance policy for harassment, verbal abuse, discrimination, or physical threats against Service Partners. Customers must provide a safe working space free from hazards. Service Partners reserve the right to decline or stop work if they reasonably perceive a safety or health risk.
              </p>
            </section>

            {/* Section 9 */}
            <section id="partner" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                9. Service Partner Independence
              </h2>
              <p>
                Service Partners are independent professionals. While Fixezy verifies government identities (Aadhaar/PAN), conducts background checks, and assesses trade skills, partners independently manage their work schedules and job executions.
              </p>
            </section>

            {/* Section 10 */}
            <section id="liability" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                10. Limitation of Liability
              </h2>
              <p>
                To the fullest extent permitted by Indian law, Fixezy is not liable for indirect, incidental, or consequential damages. In all circumstances, Fixezy's total aggregate liability arising out of or related to any booking is strictly capped at the <strong>total fee paid by you for that specific booking, or ₹5,000</strong>, whichever is lower.
              </p>
            </section>

            {/* Section 11 */}
            <section id="ip" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                11. Intellectual Property
              </h2>
              <p>
                All brand names, trademarks, app interfaces, logos, and content on the Fixezy platform are the exclusive intellectual property of Fixezy Private Limited and may not be copied, reproduced, or reverse-engineered without prior written authorization.
              </p>
            </section>

            {/* Section 12 */}
            <section id="governing-law" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                12. Governing Law & Jurisdiction
              </h2>
              <p>
                These Terms are governed by and construed in accordance with the laws of the Republic of India. Any unresolved disputes arising out of these Terms shall be subject to the exclusive jurisdiction of the competent courts in <strong>Bengaluru, Karnataka, India</strong>.
              </p>
            </section>

            {/* Section 13 */}
            <section id="contact" className="scroll-mt-28 border-t border-gray-200 pt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                13. Contact Information
              </h2>
              <div className="border border-gray-200 rounded-lg p-4 space-y-1 text-sm text-gray-800">
                <p><strong>Fixezy Private Limited</strong></p>
                <p>Vijayanagar, Bengaluru, Karnataka - 560040, India</p>
                <p>Email: support@fixezy.in</p>
                <p>Support Hours: Mon – Fri, 9:00 AM – 6:00 PM IST</p>
              </div>
            </section>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}
